import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import classes from './departments.module.scss';
import classes2 from '../files/styles.module.scss';
import { createCn } from '../../helpers/utils';
import { AdminUserItem, apiService, DepartmentItem } from '../../services/api.service';
import DepartmentList from './components/department-list.component';
import UserPriorityList from './components/user-priority-list.component';

const cn = createCn('departments', classes);
const cn2 = createCn('files', classes2);

const DepartmentsPage = () => {
  const [departments, setDepartments] = useState<DepartmentItem[]>([]);
  const [users, setUsers] = useState<AdminUserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [creatingDept, setCreatingDept] = useState(false);
  const [savingDeptId, setSavingDeptId] = useState<number | null>(null);
  const [deletingDeptId, setDeletingDeptId] = useState<number | null>(null);

  const [savingUserId, setSavingUserId] = useState<number | null>(null);
  const [savingUserField, setSavingUserField] = useState<'department' | 'priority' | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [departmentsData, usersData] = await Promise.all([
        apiService.listDepartments(),
        apiService.listAdminUsers(),
      ]);
      setDepartments(departmentsData);
      setUsers(usersData);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Не удалось загрузить отделы и пользователей');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateDepartment = async (name: string, priority: number) => {
    setCreatingDept(true);
    try {
      const created = await apiService.createDepartment({ name, priority });
      setDepartments((prev) =>
        [...prev, created].sort((a, b) => b.priority - a.priority || a.name.localeCompare(b.name)),
      );
      toast.success(`Отдел «${created.name}» создан`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось создать отдел');
    } finally {
      setCreatingDept(false);
    }
  };

  const handleUpdateDepartmentPriority = async (id: number, priority: number) => {
    setSavingDeptId(id);
    try {
      const updated = await apiService.updateDepartment(id, { priority });
      setDepartments((prev) => prev.map((d) => (d.id === id ? updated : d)));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось обновить приоритет отдела');
    } finally {
      setSavingDeptId(null);
    }
  };

  const handleDeleteDepartment = async (department: DepartmentItem) => {
    const warning = department.user_count > 0
      ? ` В нём ${department.user_count} сотрудник(ов) — после удаления они останутся без отдела (приоритет по умолчанию).`
      : '';
    if (!window.confirm(`Удалить отдел «${department.name}»?${warning}`)) return;

    setDeletingDeptId(department.id);
    try {
      await apiService.deleteDepartment(department.id);
      setDepartments((prev) => prev.filter((d) => d.id !== department.id));
      setUsers((prev) => prev.map((u) => (u.department_id === department.id ? { ...u, department_id: null } : u)));
      toast.success(`Отдел «${department.name}» удалён`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось удалить отдел');
    } finally {
      setDeletingDeptId(null);
    }
  };

  const handleChangeUserDepartment = async (userId: number, departmentId: number | null) => {
    setSavingUserId(userId);
    setSavingUserField('department');
    try {
      const updated = await apiService.updateUserDepartment(userId, departmentId);
      setUsers((prev) => prev.map((u) => (u.id === userId ? updated : u)));
      // Число сотрудников в отделах могло измениться — перечитываем список отделов
      // вместо ручного пересчёта счётчиков на клиенте.
      const departmentsData = await apiService.listDepartments();
      setDepartments(departmentsData);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось назначить отдел');
    } finally {
      setSavingUserId(null);
      setSavingUserField(null);
    }
  };

  const handleChangeUserPriority = async (userId: number, priority: number | null) => {
    setSavingUserId(userId);
    setSavingUserField('priority');
    try {
      const updated = await apiService.updateUserPriority(userId, priority);
      setUsers((prev) => prev.map((u) => (u.id === userId ? updated : u)));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось задать приоритет');
    } finally {
      setSavingUserId(null);
      setSavingUserField(null);
    }
  };

  return (
    <div className={cn2('')}>
      <div className={cn2('heading')}>Отделы и приоритеты</div>

      <div className={cn2('content')}>
        <p className={cn('subtitle')}>
          Приоритет решает порядок обработки записей в очереди: личный приоритет пользователя →
          приоритет его отдела → 50 по умолчанию. Больше — раньше.
        </p>

        {loading ? (
          <div className={cn('status')}>Загрузка…</div>
        ) : (
          <>
            {loadError && (
              <div className={cn('error-banner')}>
                {loadError}
                <button type="button" onClick={load}>Повторить</button>
              </div>
            )}

            <DepartmentList
              departments={departments}
              creating={creatingDept}
              savingId={savingDeptId}
              deletingId={deletingDeptId}
              onCreate={handleCreateDepartment}
              onUpdatePriority={handleUpdateDepartmentPriority}
              onDelete={handleDeleteDepartment}
            />

            <UserPriorityList
              users={users}
              departments={departments}
              savingUserId={savingUserId}
              savingField={savingUserField}
              onChangeDepartment={handleChangeUserDepartment}
              onChangePriority={handleChangeUserPriority}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default DepartmentsPage;
